import React from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';
import theme from '../../theme';

const IndustriesSection = styled.section`
  padding: 6rem 0 4rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1`
  font-size: ${theme.fontSizes.xxlarge};
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const PageSubtitle = styled.p`
  font-size: ${theme.fontSizes.medium};
  color: ${theme.colors.darkGray};
  max-width: 700px;
  margin: 0 auto;
`;

const FeaturedIndustry = styled.div`
  margin: 0 auto 5rem;
  max-width: 900px;
`;

const FeaturedCard = styled.div`
  border-radius: ${theme.borderRadius};
  overflow: hidden;
  box-shadow: ${theme.boxShadow};
  background-color: ${theme.colors.lightGray};
`;

const FeaturedImage = styled.div`
  height: 400px;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.7) 100%);
    z-index: 1;
  }
`;

const FeaturedContent = styled.div`
  padding: 2rem;
`;

const FeaturedTitle = styled.h2`
  font-size: ${theme.fontSizes.xlarge};
  margin-bottom: 1.5rem;
  color: ${theme.colors.secondary};
  
  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background-color: ${theme.colors.primary};
    margin-top: 0.75rem;
  }
`;

const IndustryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: ${theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const IndustryCard = styled(Link)`
  position: relative;
  display: block;
  height: 250px;
  border-radius: ${theme.borderRadius};
  overflow: hidden;
  box-shadow: ${theme.boxShadow};
  transition: ${theme.transition};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  }
  
  &:hover img {
    transform: scale(1.05);
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.7) 100%);
    z-index: 1;
    transition: ${theme.transition};
  }
  
  &:hover:before {
    background: linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.8) 100%);
  }
`;

const IndustryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
`;

const IndustryTitle = styled.h3`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 1.5rem;
  color: ${theme.colors.white};
  font-size: ${theme.fontSizes.medium};
  z-index: 2;
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin: 4rem 0 2rem;
  font-size: ${theme.fontSizes.xlarge};
  position: relative;
  display: inline-block;
  left: 50%;
  transform: translateX(-50%);
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const CTASection = styled.section`
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  padding: 4rem 0;
  margin-top: 5rem;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: ${theme.fontSizes.xlarge};
  margin-bottom: 1.5rem;
`;

const CTAText = styled.p`
  max-width: 700px;
  margin: 0 auto 2rem;
  font-size: ${theme.fontSizes.medium};
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  padding: 1rem 2rem;
  font-size: ${theme.fontSizes.medium};
  font-weight: 600;
  border-radius: ${theme.borderRadius};
  transition: ${theme.transition};
  
  &:hover {
    background-color: ${theme.colors.accent};
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const IndustriesSolutionsPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  
  const featuredIndustry = {
    titleKey: 'industries.featured.title',
    image: `${process.env.PUBLIC_URL}/food-featured-title-bg.jpeg`,
    path: `/${lang}/industries/food`
  };
  
  const allIndustries = [
    {
      titleKey: 'industries.items.food',
      image: `${process.env.PUBLIC_URL}/industries-food.jpeg`,
      path: `/${lang}/industries/food`
    },
    {
      titleKey: 'industries.items.automotive',
      image: `${process.env.PUBLIC_URL}/industries-automotive.jpeg`,
      path: `/${lang}/industries/automotive`
    },
    {
      titleKey: 'industries.items.tire',
      image: `${process.env.PUBLIC_URL}/industries-tire.jpeg`,
      path: `/${lang}/industries/tire`
    },
    {
      titleKey: 'industries.items.textile',
      image: `${process.env.PUBLIC_URL}/industries-textile.jpeg`,
      path: `/${lang}/industries/textile`
    },
    {
      titleKey: 'industries.items.construction',
      image: `${process.env.PUBLIC_URL}/industries-construction.jpeg`,
      path: `/${lang}/industries/construction`
    },
    {
      titleKey: 'industries.items.home',
      image: `${process.env.PUBLIC_URL}/industries-home.jpeg`,
      path: `/${lang}/industries/home`
    },
    {
      titleKey: 'industries.items.electronics',
      image: `${process.env.PUBLIC_URL}/industries-electronics.jpeg`,
      path: `/${lang}/industries/electronics`
    },
    {
      titleKey: 'industries.items.publishing',
      image: `${process.env.PUBLIC_URL}/industries-publishing.jpeg`,
      path: `/${lang}/industries/publishing`
    },
    {
      titleKey: 'industries.items.optical',
      image: `${process.env.PUBLIC_URL}/industries-optical.jpeg`,
      path: `/${lang}/industries/optical`
    }
  ];
  
  return (
    <>
      <SEO 
        titleKey="industriesSolutions.seo.title"
        descriptionKey="industriesSolutions.seo.description"
      />
      
      <IndustriesSection>
        <div className="container">
          <PageHeader>
            <PageTitle>{t('industriesSolutions.title')}</PageTitle>
            <PageSubtitle>
              {t('industriesSolutions.subtitle')}
            </PageSubtitle>
          </PageHeader>
          
          <FeaturedIndustry>
            <FeaturedCard>
              <FeaturedImage>
                <img src={featuredIndustry.image} alt={t(featuredIndustry.titleKey)} loading="lazy" />
              </FeaturedImage>
              <FeaturedContent>
                <FeaturedTitle>{t('industriesSolutions.featured.title')}</FeaturedTitle>
                <p>{t('industriesSolutions.featured.description1')}</p>
                <p>{t('industriesSolutions.featured.description2')}</p>
                <CTAButton to={featuredIndustry.path}>
                  {t('industriesSolutions.featured.button')}
                </CTAButton>
              </FeaturedContent>
            </FeaturedCard>
          </FeaturedIndustry>
          
          <SectionTitle>{t('industriesSolutions.allTitle')}</SectionTitle>
          
          <PageSubtitle>
            {t('industriesSolutions.allSubtitle')}
          </PageSubtitle>
          
          <IndustryGrid>
            {allIndustries.map((industry, index) => (
              <IndustryCard key={index} to={industry.path}>
                <IndustryImage src={industry.image} alt={t(industry.titleKey)} loading="lazy" />
                <IndustryTitle>{t(industry.titleKey)}</IndustryTitle>
              </IndustryCard>
            ))}
          </IndustryGrid>
        </div>
      </IndustriesSection>
      
      <CTASection>
        <div className="container">
          <CTATitle>{t('industriesSolutions.cta.title')}</CTATitle>
          <CTAText>
            {t('industriesSolutions.cta.description')}
          </CTAText>
          <CTAButton to={`/${lang}/contact`}>{t('industriesSolutions.cta.button')}</CTAButton>
        </div>
      </CTASection>
    </>
  );
};

export default IndustriesSolutionsPage; 